import { format, isToday } from "date-fns";
import type { Locale } from "date-fns";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useLocaleState, useTranslate } from "ra-core";
import { useMemo, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router";

import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";
import { SegmentedControl } from "@/components/atomic-crm/tasks/SegmentedControl";

import { CALENDAR_SOURCES, type CalendarSource } from "../common/constants";
import type { CalendarItem } from "../types";
import {
  buildMonthGrid,
  buildWeekGrid,
  daySources,
  itemPath,
  type MonthGridDay,
} from "./calendarModel";

import "@/styles/mobile-glass.css";

export type CalendarView = "month" | "week" | "agenda";

const ALL_SOURCES = Object.keys(CALENDAR_SOURCES) as CalendarSource[];
/** Horizontal travel (px) that turns a drag on the grid into a month swipe. */
const SWIPE_THRESHOLD = 48;

interface MobileCalendarProps {
  title: string;
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;
  cursor: Date;
  onCursorChange: (date: Date) => void;
  onMove: (direction: 1 | -1) => void;
  sources: CalendarSource[];
  onSourcesChange: (sources: CalendarSource[]) => void;
  byDay: Map<string, CalendarItem[]>;
  dfLocale: Locale;
  onCreate: (day: string) => void;
}

/**
 * Phone calendar, iOS style: a compact month (or week) grid with one dot per
 * item type, and the selected day's items listed below it. Swipe the grid or
 * use the arrows to change month.
 */
export const MobileCalendar = ({
  title,
  view,
  onViewChange,
  cursor,
  onCursorChange,
  onMove,
  sources,
  onSourcesChange,
  byDay,
  dfLocale,
  onCreate,
}: MobileCalendarProps) => {
  const translate = useTranslate();
  const [picked, setPicked] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [direction, setDirection] = useState<"next" | "prev" | undefined>();
  const weeks = useMemo(
    () => (view === "week" ? buildWeekGrid(cursor) : buildMonthGrid(cursor)),
    [cursor, view],
  );
  const selected = resolveSelected(weeks, picked);

  const move = (step: 1 | -1) => {
    setDirection(step === 1 ? "next" : "prev");
    onMove(step);
  };

  const goToday = () => {
    setDirection(undefined);
    setPicked(format(new Date(), "yyyy-MM-dd"));
    onCursorChange(new Date());
  };

  const pick = (day: MonthGridDay) => {
    setPicked(day.key);
    if (!day.inMonth) {
      setDirection(day.date < cursor ? "prev" : "next");
      onCursorChange(day.date);
    }
  };

  const touch = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (event: TouchEvent) => {
    const point = event.touches[0];
    touch.current = { x: point.clientX, y: point.clientY };
  };
  const onTouchEnd = (event: TouchEvent) => {
    const start = touch.current;
    touch.current = null;
    if (!start) return;
    const point = event.changedTouches[0];
    const dx = point.clientX - start.x;
    const dy = point.clientY - start.y;
    if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.5)
      move(dx < 0 ? 1 : -1);
  };

  const prevLabel =
    view === "week"
      ? translate("crm.mobile.week_previous")
      : translate("crm.mobile.month_previous");
  const nextLabel =
    view === "week"
      ? translate("crm.mobile.week_next")
      : translate("crm.mobile.month_next");

  return (
    <div className="-mx-4">
      <MobileHeader>
        <h1 className="min-w-0 flex-1 truncate text-2xl font-semibold tracking-tight first-letter:uppercase">
          {title}
        </h1>
        <div className="liquid-glass mcal-nav">
          <button type="button" onClick={() => move(-1)} aria-label={prevLabel}>
            <ChevronLeft className="size-5" />
          </button>
          <button type="button" onClick={goToday}>
            {translate("hub.calendar.today")}
          </button>
          <button type="button" onClick={() => move(1)} aria-label={nextLabel}>
            <ChevronRight className="size-5" />
          </button>
        </div>
      </MobileHeader>

      <div className="home-m px-4 pb-2">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <SegmentedControl
              glass
              className="flex-1"
              label={translate("crm.mobile.agenda_view")}
              value={view}
              onChange={onViewChange}
              options={(["month", "week", "agenda"] as const).map((v) => ({
                value: v,
                label: translate(`hub.calendar.${v}`),
              }))}
            />
            <Link
              to="/events/create"
              aria-label={translate("hub.calendar.new_event")}
              className="agenda-accent glass-press inline-flex size-10 shrink-0 items-center justify-center rounded-full shadow-md"
            >
              <Plus className="size-5" />
            </Link>
          </div>
          <SourceChips sources={sources} onChange={onSourcesChange} />
        </div>

        {view === "agenda" ? (
          <AgendaList byDay={byDay} />
        ) : (
          <>
            <div
              className="mcal"
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
            >
              <div className="mcal__weekdays" aria-hidden>
                {weeks[0].map((day) => (
                  <span key={day.key}>
                    {format(day.date, "EEEEE", { locale: dfLocale })}
                  </span>
                ))}
              </div>
              <div
                key={weeks[0][0].key}
                className="mcal__grid"
                data-dir={direction}
              >
                {weeks.map((week) => (
                  <div key={week[0].key} className="mcal__week">
                    {week.map((day) => (
                      <DayCell
                        key={day.key}
                        day={day}
                        items={byDay.get(day.key) ?? []}
                        isSelected={day.key === selected}
                        onPick={pick}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <DayList
              day={selected}
              items={byDay.get(selected) ?? []}
              onCreate={onCreate}
            />
          </>
        )}
      </div>
    </div>
  );
};

/** The picked day if it is on screen, else today, else the first day of the period. */
const resolveSelected = (weeks: MonthGridDay[][], picked: string): string => {
  const days = weeks.flat();
  const inPeriod = days.filter((day) => day.inMonth);
  if (inPeriod.some((day) => day.key === picked)) return picked;
  const today = format(new Date(), "yyyy-MM-dd");
  if (inPeriod.some((day) => day.key === today)) return today;
  return (inPeriod[0] ?? days[0]).key;
};

const DayCell = ({
  day,
  items,
  isSelected,
  onPick,
}: {
  day: MonthGridDay;
  items: CalendarItem[];
  isSelected: boolean;
  onPick: (day: MonthGridDay) => void;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const label = `${new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(day.date)}, ${translate("crm.mobile.agenda_items", {
    smart_count: items.length,
  })}`;
  return (
    <button
      type="button"
      className="mcal__day"
      onClick={() => onPick(day)}
      aria-label={label}
      aria-pressed={isSelected}
      data-cal-day={day.key}
      data-has-items={items.length > 0 ? "true" : "false"}
      data-out={day.inMonth ? undefined : "true"}
      data-today={isToday(day.date) ? "true" : undefined}
      data-selected={isSelected ? "true" : undefined}
    >
      <span className="mcal__num">{format(day.date, "d")}</span>
      <span className="mcal__dots" aria-hidden>
        {daySources(items).map((source) => (
          <span
            key={source}
            style={{ backgroundColor: CALENDAR_SOURCES[source].color }}
          />
        ))}
      </span>
    </button>
  );
};

const SourceChips = ({
  sources,
  onChange,
}: {
  sources: CalendarSource[];
  onChange: (sources: CalendarSource[]) => void;
}) => {
  const translate = useTranslate();
  return (
    <div
      role="group"
      aria-label={translate("hub.calendar.sources_label")}
      className="home-chips py-1"
    >
      {ALL_SOURCES.map((source) => {
        const active = sources.includes(source);
        return (
          <button
            key={source}
            type="button"
            aria-pressed={active}
            data-off={active ? undefined : "true"}
            onClick={() =>
              onChange(
                active
                  ? sources.filter((s) => s !== source)
                  : [...sources, source],
              )
            }
            className="glass-chip"
          >
            <span
              aria-hidden
              className="glass-chip__dot"
              style={{ backgroundColor: CALENDAR_SOURCES[source].color }}
            />
            {translate(`hub.calendar.sources.${source}`)}
          </button>
        );
      })}
    </div>
  );
};

const useDayHeading = () => {
  const [locale = "es"] = useLocaleState();
  return useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
      }),
    [locale],
  );
};

const DayList = ({
  day,
  items,
  onCreate,
}: {
  day: string;
  items: CalendarItem[];
  onCreate: (day: string) => void;
}) => {
  const translate = useTranslate();
  const heading = useDayHeading();
  const date = new Date(`${day}T00:00:00`);
  return (
    <section
      className="home-m__section"
      aria-labelledby="mcal-day"
      aria-live="polite"
    >
      <div className="home-m__head">
        <h2 id="mcal-day" className="first-letter:uppercase">
          {heading.format(date)}
        </h2>
        {items.length > 0 ? (
          <span className="text-sm text-muted-foreground">
            {translate("crm.mobile.agenda_items", {
              smart_count: items.length,
            })}
          </span>
        ) : null}
      </div>
      <div className="home-m__card">
        {items.length === 0 ? (
          <div className="flex items-center justify-between gap-3 py-4">
            <span className="text-sm text-muted-foreground">
              {translate("crm.mobile.no_events")}
            </span>
            <button
              type="button"
              onClick={() => onCreate(day)}
              className="glass-chip h-8 px-3 text-[13px]"
            >
              <Plus aria-hidden className="size-4" />
              {translate("hub.calendar.new_event")}
            </button>
          </div>
        ) : (
          items.map((item) => <ItemRow key={item.id} item={item} />)
        )}
      </div>
    </section>
  );
};

const ItemRow = ({ item }: { item: CalendarItem }) => {
  const translate = useTranslate();
  return (
    <Link
      to={itemPath(item)}
      className="mcal-list__row"
      data-done={item.status === "done" ? "true" : undefined}
    >
      <span
        aria-hidden
        className="mcal-list__bar"
        style={{ backgroundColor: CALENDAR_SOURCES[item.source].color }}
      />
      <span className="mcal-list__time">
        {item.all_day
          ? translate("crm.mobile.all_day")
          : format(new Date(item.start_at), "HH:mm")}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="mcal-list__title">{item.title}</span>
        <span className="mcal-list__kind">
          {translate(`hub.calendar.sources.${item.source}`)}
        </span>
      </span>
      <ChevronRight
        aria-hidden
        className="size-4 text-muted-foreground"
        strokeWidth={2}
      />
    </Link>
  );
};

const AgendaList = ({ byDay }: { byDay: Map<string, CalendarItem[]> }) => {
  const translate = useTranslate();
  const heading = useDayHeading();
  const days = [...byDay.keys()].sort();
  if (days.length === 0) {
    return (
      <div className="home-m__card">
        <p className="home-empty">{translate("hub.calendar.no_items")}</p>
      </div>
    );
  }
  return (
    <>
      {days.map((day) => (
        <section key={day} className="home-m__section">
          <div className="home-m__head">
            <h2 className="first-letter:uppercase">
              {heading.format(new Date(`${day}T00:00:00`))}
            </h2>
          </div>
          <div className="home-m__card">
            {byDay.get(day)!.map((item) => (
              <ItemRow key={item.id} item={item} />
            ))}
          </div>
        </section>
      ))}
    </>
  );
};
